import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, Phone, MessageCircle, HelpCircle, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';

const faqCategories = [
  {
    title: 'General Questions',
    faqs: [
      {
        q: 'What services does Zafco Human Resource Management offer?',
        a: 'Zafco provides comprehensive overseas manpower recruitment services including work visa assistance, skilled migration support for countries like Canada, Australia, USA, UK, Germany, and UAE. We also offer business/investor visa guidance, study abroad consultation, job seeker visa assistance, resume writing services, and IELTS coaching.',
      },
      {
        q: 'How long has Zafco been in the overseas recruitment industry?',
        a: 'Zafco Human Resource Management has over 30 years of combined experience in the overseas recruitment industry. Founded by Mr. Zafar Abdul Wahhab, we have successfully assisted over 10,000 clients in achieving their international career goals.',
      },
      {
        q: 'Is Zafco a registered immigration consultancy?',
        a: 'Yes, Zafco Human Resource Management is a registered overseas manpower recruitment consultancy operating from New Delhi, India. We maintain all necessary licenses and follow ethical recruitment practices.',
      },
      {
        q: 'What is your success rate for visa applications?',
        a: 'Our success rate for visa applications is approximately 98%, one of the highest in the industry. This is achieved through thorough documentation, expert guidance, and personalized attention to each client\'s case.',
      },
    ],
  },
  {
    title: 'Canada Immigration',
    faqs: [
      {
        q: 'What is Canada Express Entry and how does it work?',
        a: 'Canada Express Entry is a points-based immigration system that manages applications for three federal economic immigration programs: Federal Skilled Worker Program (FSWP), Canadian Experience Class (CEC), and Federal Skilled Trades Program (FSTP). Candidates are ranked using the Comprehensive Ranking System (CRS), and top-scoring candidates receive Invitations to Apply (ITA) for permanent residency.',
      },
      {
        q: 'What CRS score do I need for Canada Express Entry in 2026?',
        a: 'CRS cutoff scores vary by draw type. General draws typically have cutoffs between 480-520 points, while category-based draws (healthcare, STEM, trades) may have lower cutoffs around 400-450. Having a provincial nomination adds 600 points, significantly improving your chances.',
      },
      {
        q: 'How long does Canada PR processing take?',
        a: 'Canada Express Entry PR applications are typically processed within 6-12 months. After receiving an ITA, you have 60 days to submit your complete application. Processing times may vary based on individual circumstances and document completeness.',
      },
      {
        q: 'What is the Provincial Nominee Program (PNP)?',
        a: 'PNP allows Canadian provinces to nominate candidates who wish to settle in their province. Each province has its own streams and criteria. A provincial nomination adds 600 CRS points, making it easier to receive an ITA through Express Entry.',
      },
    ],
  },
  {
    title: 'Australia Immigration',
    faqs: [
      {
        q: 'What are the main skilled visa options for Australia?',
        a: 'The main skilled visa categories are: Subclass 189 (Skilled Independent - no sponsorship needed), Subclass 190 (Skilled Nominated - state nomination), and Subclass 491 (Skilled Work Regional - provisional visa). Each has different requirements and benefits.',
      },
      {
        q: 'How does the Australia points system work?',
        a: 'Australia uses a points-based system where you need minimum 65 points to be eligible. Points are awarded for age (max 30), English proficiency (max 20), work experience (max 20), education (max 20), and other factors like Australian study, partner skills, and state nomination.',
      },
      {
        q: 'What is a skills assessment for Australia PR?',
        a: 'Skills assessment is a mandatory step where an assessing authority evaluates if your qualifications and work experience meet Australian standards for your nominated occupation. Different occupations have different assessing bodies (e.g., ACS for IT, Engineers Australia for engineers).',
      },
    ],
  },
  {
    title: 'USA Immigration',
    faqs: [
      {
        q: 'What is the EB-5 Investor Visa?',
        a: 'The EB-5 Immigrant Investor Program allows foreign investors to obtain US permanent residency (Green Card) by investing $800,000 in a Targeted Employment Area (TEA) or $1,050,000 in a standard area, while creating at least 10 full-time jobs for US workers.',
      },
      {
        q: 'How long does EB-5 processing take?',
        a: 'EB-5 processing typically takes 4-5 years from investment to permanent Green Card. This includes I-526E petition approval (18-24 months), consular processing (6-12 months), conditional residency (2 years), and I-829 petition (12-18 months).',
      },
      {
        q: 'What is the difference between Direct and Regional Center EB-5?',
        a: 'Direct EB-5 requires you to manage the business directly, while Regional Center EB-5 allows passive investment through USCIS-approved regional centers. Regional Center investments can count indirect job creation, making it easier to meet the job creation requirement.',
      },
    ],
  },
  {
    title: 'Germany & Europe',
    faqs: [
      {
        q: 'What is the Germany Opportunity Card?',
        a: 'The Germany Opportunity Card (Chancenkarte) is a points-based job seeker visa allowing non-EU nationals to enter Germany for up to one year to search for employment. You need 6 points minimum based on qualifications, experience, language skills, and age.',
      },
      {
        q: 'What is the EU Blue Card?',
        a: 'The EU Blue Card is a work permit for highly skilled non-EU workers in Germany and other EU countries. It requires a recognized degree, job offer with minimum salary threshold (€43,992 for shortage occupations, €56,400 for others in Germany), and can lead to permanent residency.',
      },
      {
        q: 'Do I need to know German for Germany immigration?',
        a: 'For the Opportunity Card, basic German (A1) or English (B2) is acceptable. For the EU Blue Card, language requirements depend on the job. For permanent residency, German proficiency (B1-B2) is typically required.',
      },
    ],
  },
  {
    title: 'Process & Fees',
    faqs: [
      {
        q: 'What are Zafco\'s service fees?',
        a: 'Our fees vary based on the service type and destination country. We offer transparent pricing with no hidden charges. A free initial consultation is provided to assess your profile and provide a detailed fee structure based on your specific requirements.',
      },
      {
        q: 'Do you offer refunds if the visa is rejected?',
        a: 'Our refund policy varies by service package. We offer certain refund provisions for visa rejections not caused by client negligence or misrepresentation. Please discuss specific terms during your consultation.',
      },
      {
        q: 'How long does your assessment process take?',
        a: 'Our initial profile assessment is completed within 24-48 hours. After you submit your documents, we provide a detailed eligibility report with recommendations for the best immigration pathways based on your profile.',
      },
      {
        q: 'Do I need IELTS for overseas job placement?',
        a: 'Most countries require English language proficiency tests like IELTS, PTE, or TOEFL for immigration purposes. Requirements vary by country and visa category. Some countries like Germany may accept alternative language tests or waive requirements in certain cases.',
      },
    ],
  },
];

const FAQPage = () => {
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20have%20a%20question%20about%20overseas%20recruitment', '_blank');
  };

  return (
    <div data-testid="faq-page">
      <PageHeader 
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about overseas recruitment, immigration, and our services"
        breadcrumbs={[{ label: 'FAQ' }]}
      />

      {/* FAQ Content */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Quick Contact */}
            <motion.div
              className="bg-gradient-to-br from-zafco-blue to-zafco-teal rounded-2xl p-8 mb-12 text-white"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                    <HelpCircle className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Can't find your answer?</h3>
                    <p className="text-white/80">Our experts are just a message away</p>
                  </div>
                </div>
                <Button 
                  onClick={openWhatsApp}
                  className="bg-white text-zafco-blue hover:bg-gray-100 font-semibold px-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Ask on WhatsApp
                </Button>
              </div>
            </motion.div>

            {/* FAQ Categories */}
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                className="mb-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
              >
                <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-6">
                  {category.title}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faq.q}
                      value={`${category.title}-${faqIndex}`}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm px-6"
                      data-testid={`faq-${categoryIndex}-${faqIndex}`}
                    >
                      <AccordionTrigger className="text-left text-zafco-blue font-semibold hover:no-underline py-5">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}

            {/* CTA Section */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-8 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-zafco-blue font-montserrat mb-4">
                Ready to Start Your Global Career Journey?
              </h3>
              <p className="text-gray-600 mb-6">
                Get your free profile assessment and discover the best overseas opportunities for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={openWhatsApp}
                  className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Assessment
                </Button>
                <Link to="/contact">
                  <Button variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue hover:text-white">
                    Contact Us <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
